import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStickyNote } from '@fortawesome/free-solid-svg-icons';

/**
 * Screen-3 notes block: the editable pair (notes + "show on the printout" tick)
 * while the document is still ours to change, a plain read-only rendering once
 * it is approved.
 *
 * Why this is a component rather than five copies:
 *  - IncomingInvoiceScreen3 never guarded its notes at all, so an approved and
 *    paid supplier invoice could still be retyped.
 *  - Outgoing and pro-forma used `readOnly` on the textarea, and
 *    OutgoingInvoice.css deliberately styles `textarea[readonly]` to look
 *    IDENTICAL to an editable one (white background, text cursor, blue focus
 *    ring) - so the lock was invisible and users typed into a dead box.
 *  - Credit/debit notes hid the visibility tick entirely when locked, which
 *    hides whether the notes print or not.
 *
 * One component means the next screen cannot forget the lock.
 */
const InvoiceNotesField = ({
  label = 'Invoice Notes',
  id,
  value,
  onChange,
  placeholder = 'Enter invoice notes...',
  isReadOnly = false,
  rows = 8,
  fillHeight = false,
  // Optional "notes visible on the printout" tick. Incoming invoices and
  // purchase orders keep internal-only notes and pass nothing here.
  visibilityLabel,
  visibilityValue,
  onVisibilityChange,
}) => {
  const hasVisibilityToggle = typeof onVisibilityChange === 'function';
  const fieldClass = fillHeight ? 'form-field-flexible' : 'form-field';
  const fillStyle = fillHeight
    ? { height: '100%', display: 'flex', flexDirection: 'column' }
    : undefined;

  return (
    <>
      <div className={fieldClass} style={fillStyle}>
        <label className="field-label" htmlFor={id}>
          <FontAwesomeIcon icon={faStickyNote} /> {label}
        </label>

        {isReadOnly ? (
          <div
            id={id}
            className="form-display-value"
            data-testid="notes-locked"
            style={{
              flex: fillHeight ? 1 : undefined,
              minHeight: '200px',
              whiteSpace: 'pre-wrap',
              alignItems: 'flex-start',
            }}
          >
            {value || 'N/A'}
          </div>
        ) : (
          <textarea
            id={id}
            className="form-textarea-large"
            style={fillHeight ? { flex: 1, minHeight: '200px' } : undefined}
            value={value || ''}
            onChange={onChange}
            placeholder={placeholder}
            rows={rows}
          />
        )}
      </div>

      {hasVisibilityToggle && (
        <div className="form-field" style={{ marginTop: '12px' }}>
          {isReadOnly ? (
            /* Still say whether the notes print - hiding the tick hides the answer. */
            <div className="form-display-value" data-testid="notes-visibility-locked">
              {visibilityLabel}: <strong style={{ marginLeft: '6px' }}>{visibilityValue ? 'Yes' : 'No'}</strong>
            </div>
          ) : (
            <label className="checkbox-label" htmlFor={`${id}-visible`}>
              <input
                id={`${id}-visible`}
                type="checkbox"
                checked={visibilityValue || false}
                onChange={onVisibilityChange}
              />
              <span>{visibilityLabel}</span>
            </label>
          )}
        </div>
      )}
    </>
  );
};

export default InvoiceNotesField;
